import React, { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CreateTaskDrawer from './CreateTaskDrawer';
import TaskResultDrawer from './TaskResultDrawer';
import Box from '@mui/joy/Box';
import Checkbox from '@mui/joy/Checkbox';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import request from '../helpers/request'
import { useSelector, useDispatch } from 'react-redux';
import { getTasks, setSearch, setPagination, getCompletedTasksToday, setFilters, getPolicies } from '../redux/policiesReducer';
import { Select, FormLabel, FormControl, Sheet, Input, Modal, Card, Table, ModalDialog, DialogTitle, DialogContent, ModalClose, AccordionGroup, Accordion, CardContent, Divider, IconButton, Tooltip, List, ListItem, Chip, Option, DialogActions, Grid, Link } from '@mui/joy';
import AccordionSummary, {
	accordionSummaryClasses,
} from '@mui/joy/AccordionSummary';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import AccordionDetails, { accordionDetailsClasses } from '@mui/joy/AccordionDetails';
import ReorderOutlinedIcon from '@mui/icons-material/ReorderOutlined';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import { useNavigate } from 'react-router-dom';
import { Stack, CircularProgress } from '@mui/joy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { toast } from "react-toastify";

// Add CSS keyframes for pulse animation
const pulseAnimation = `
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}
`;

// Inject pulse animation CSS into document
if (typeof document !== 'undefined') {
	const styleId = 'pulse-animation-style';
	if (!document.getElementById(styleId)) {
		const styleEl = document.createElement('style');
		styleEl.id = styleId;
		styleEl.textContent = pulseAnimation;
		document.head.appendChild(styleEl);
	}
}

// Skeleton box style with pulse animation
const skeletonBoxSx = {
	bgcolor: 'background.level2',
	borderRadius: 1,
	animation: 'pulse 1.5s ease-in-out infinite',
};

const TaskReportsTable = ({
	startTask,
	setStartTask,
	loading,
	currentPolicy,
	setCurrentPolicy,
	currentOutputPolicy,
	setCurrentOutputPolicy,
	selectedFiles,
	setSelectedFiles,
	handleFileChange,
	handleRemoveFile,
	handleSubmit,
	referencedObjects,
	setReferencedObjects,
	policiesFromProps,
	pages,
	currentPage,
	setCurrentPage,
	isPoliciesLoading,
	refreshTable,
}) => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const [rerunningTasks, setRerunningTasks] = useState(new Set());
	const [selectedTaskIds, setSelectedTaskIds] = useState([]);

	// Expansion state for inline task result drawer
	const [expandedTaskId, setExpandedTaskId] = useState(null);
	const [closingTaskId, setClosingTaskId] = useState(null);
	const [expandedTaskResult, setExpandedTaskResult] = useState(null);
	const [expandedTaskInput, setExpandedTaskInput] = useState(null);
	const [isLoadingTaskResult, setIsLoadingTaskResult] = useState(false);
	const tableScrollContainerRef = useRef(null);
	const expandedTaskRowRef = useRef(null);

	// getTasks
	const [statuses, setStatuses] = useState([]);

	const tableHeaders = [
		{
			name: "Task",
			key: "id",
			filterLabel: "Task ID",
			filterType: "number",
		},
		{
			name: "Status",
			key: "status",
			filterLabel: "Status",
			filterType: "status",
		},
		{
			name: "Policy",
			key: "policy_name",
			filterLabel: "Policy Name",
			filterType: "policy",
		},
		{
			name: "Duration",
			key: "duration",
			filterLabel: "Duration",
			filterType: "duration",
		},
		{
			name: "Start Time",
			key: "created_at",
			filterLabel: "Start Date",
			filterType: "date",
		},
		{
			name: "End Time",
			key: "last_modified",
			filterLabel: "End Date",
			filterType: "date",
		},
		{
			name: null,
			key: "actions",
		},
	];

	const taskStatus = {
		created: 'neutral',
		'in progress': 'primary',
		'action required': 'warning',
		completed: 'success',
		failed: 'danger',
	};

	const [filterStates, setFilterStates] = useState(
		Object.fromEntries(
			tableHeaders.map(h => [
				h.key,
				h.filterType === 'duration'
					? { open: false, anchorEl: null, op: 'gt', val: '', unit: 'seconds' }
					: { open: false, anchorEl: null, value: '' },
			])
		)
	);


	const filters = useSelector((state) => state.policies.pagination.filters);

	const handlePopperToggle = (key, event) => {
		const anchor = event.currentTarget;
		setFilterStates(prev => {
			const newState = {};
			for (const k in prev) {
				newState[k] = { ...prev[k], open: false, anchorEl: null };
			}
			newState[key] = {
				...newState[key],
				open: !prev[key]?.open,
				anchorEl: anchor,
				value: filters?.[key] || '',
			};
			return newState;
		});
	};



	const handleInputChange = (key, value, subKey = 'value') => {
		setFilterStates(prev => ({
			...prev,
			[key]: {
				...prev[key],
				[subKey]: value,
			},
		}));
	};

	const handleApply = (key, stateOverride) => {
		const state = stateOverride ?? filterStates[key];

		let filterChanges = {};

		if (key === 'duration') {
			const unit = state.unit || 'seconds';
			let multiplier = 1;
			if (unit === 'minutes') multiplier = 60;
			else if (unit === 'hours') multiplier = 3600;

			const numericVal = parseFloat(state.val);
			const computedSeconds = isNaN(numericVal) ? '' : numericVal * multiplier;

			// Don't apply if both op and val are empty
			if (!state.op || !state.val || state.val.trim() === '') {
				return;
			}

			filterChanges = {
				duration_op: state.op,
				duration_val: computedSeconds,
			};
		}
		else if (tableHeaders.find(h => h.key === key && h.filterType === 'date')) {
			// Don't apply if both op and val are empty
			if (!state.op || !state.val || state.val.trim() === '') {
				return;
			}

			filterChanges = {
				[`${key}_op`]: state.op,
				[`${key}_val`]: state.val
			};
		} else {
			// Don't apply if value is empty
			if (!state.value || state.value.trim() === '') {
				return;
			}

			filterChanges = {
				[key]: state.value
			};
		}

		handleFilterChange(filterChanges); // <- only one dispatch inside this
		setFilterStates(prev => ({
			...prev,
			[key]: {
				...prev[key],
				open: false,
			},
		}));
	};


	const handleClear = (key, filterType) => {
		let filterChanges = {};

		// Clear the filter state
		setFilterStates(prev => {
			const newState = { ...prev };

			switch (filterType) {
				case 'duration':
					newState[key] = { ...prev[key], op: '', val: '', open: false, anchorEl: null };
					filterChanges = {
						duration_op: '',
						duration_val: '',
					};
					break;
				case 'date':
					newState[key] = { ...prev[key], op: '', val: '', open: false, anchorEl: null };
					filterChanges = {
						[`${key}_op`]: '',
						[`${key}_val`]: '',
					};
					break;
				case 'text':
				case 'number':
				case 'status':
				case 'policy':
					newState[key] = { ...prev[key], value: '', open: false, anchorEl: null };
					filterChanges = {
						[key]: '',
					};
					break;
				default:
					newState[key] = { ...prev[key], open: false, anchorEl: null };
					break;
			}

			return newState;
		});

		// Immediately apply the cleared filter
		handleFilterChange(filterChanges);
	};

	const isFilterActive = (key, filterType) => {
		if (filterType === 'duration' || filterType === 'date') {
			return filters?.[`${key}_op`] && filters?.[`${key}_val`];
		}
		return !!filters?.[key];
	};

	const isApplyDisabled = (key, filterType) => {
		const state = filterStates[key];
		if (!state) return true;

		if (filterType === 'duration') {
			return !state.op || !state.val || state.val.trim() === '';
		}
		if (filterType === 'date') {
			return !state.op || !state.val || state.val.trim() === '';
		}
		// For text, number, status, policy filters
		return !state.value || state.value.trim() === '';
	};



	const { tasks, isTasksLoading, pagination, completedTasksToday, isCompletedTasksLoading, hasFetchedTasks } = useSelector(state => state.policies)
	const policies = useSelector((state) => state.policies.policies);

	const getPageIndicator = `${pagination.page} - ${Math.ceil(pagination.count / pagination.page_size)}`;

	const calculateDuration = (seconds) => {
		if (isNaN(seconds) || seconds < 0) return "Invalid duration";

		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const remainingSeconds = Math.floor(seconds % 60);

		const parts = [];
		if (hours > 0) parts.push(`${hours} hr${hours !== 1 ? 's' : ''}`);
		if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);
		if (remainingSeconds > 0 || parts.length === 0) {
			parts.push(`${remainingSeconds} sec${remainingSeconds !== 1 ? 's' : ''}`);
		}

		return parts.join(', ');
	}

	const formatTimestamp = (timestamp) => {
		const date = new Date(timestamp);
		return date.toISOString().slice(0, 19).replace("T", " ");
	};

	const isObject = (variable) => typeof variable === 'object' && variable !== null && !Array.isArray(variable);

	const getMetadata = (obj) => {
		return obj.output.metadata || obj.output
	}

	const handlePreviousPage = () => {
		dispatch(setPagination({ page: pagination.page - 1 }))
		dispatch(getTasks());
	}

	const handleNextPage = () => {
		dispatch(setPagination({ page: pagination.page + 1 }))
		dispatch(getTasks());
	}

	const handlePageSize = (value) => {
		dispatch(setPagination({ page_size: value }));
		dispatch(getTasks());
	}

	const handleOrder = (key) => {
		const { ordering, isDescending } = pagination;

		if (ordering !== key) {
			dispatch(setPagination({ ordering: key, isDescending: false }));
		} else if (!isDescending) {
			dispatch(setPagination({ ordering: key, isDescending: true }));
		} else {
			dispatch(setPagination({ ordering: null, isDescending: false }));
		}

		dispatch(getTasks());
	};



	const [taskToDelete, setTaskToDelete] = useState(null);

	const handleDeleteTask = async () => {
		try {
			await request.delete(`/api/v1/tools/task/${taskToDelete.id}/`);
			dispatch(getTasks()); // Refresh the tasks list
		} catch (e) {
			console.error("Error deleting task:", e);
			alert("Failed to archive task");
		}
		setTaskToDelete(null); // Reset the taskToDelete state
	};


	const [dialogType, setDialogType] = useState("");
	const [currentTask, setCurrentTask] = useState(null);
	const [currentResult, setCurrentResult] = useState(null);
	const [open, setOpen] = useState(false);
	const [input, setInput] = useState(null);

	const fetchInput = useCallback(async (id) => {
		try {
			const inputsUrl = `/api/v1/tools/task/${id}/`;
			const response = await request.get(inputsUrl);
			return response.data;
		} catch (e) {
			console.error("Error fetching input data", e);
			return null;
		}
	}, []);

	const onOpenDocument = () => {
		if (!currentTask) return
		let base64Document = ''
		if (currentTask.payload.document.type === 'pdf')
			base64Document = 'data:application/pdf;base64,'
		else
			base64Document = 'data:image/png;base64,'
		base64Document += currentTask.payload.document.base64
		const newTab = window.open();
		newTab.document.body.innerHTML = `<iframe src="${base64Document}" width="100%" height="100%" style="border:none;"></iframe>`;
	}

	const onOpenDialog = useCallback(async (type, task) => {
		switch (type) {
			case "result":
				navigate(`/app/tasks/result/${task.id}`);
				break;
			case "logs":
				const fetchedInput = await fetchInput(task.id);
				setInput(fetchedInput);
				setOpen(true);
				break;
			default:
				return;
		}
	}, [fetchInput, navigate]);

	const scrollExpandedTaskRowIntoView = useCallback(() => {
		const scrollContainer = tableScrollContainerRef.current;
		const expandedTaskRow = expandedTaskRowRef.current;

		if (!scrollContainer || !expandedTaskRow) {
			return;
		}

		const containerRect = scrollContainer.getBoundingClientRect();
		const rowRect = expandedTaskRow.getBoundingClientRect();
		const tableHead = scrollContainer.querySelector('thead');
		const stickyHeaderHeight = tableHead?.getBoundingClientRect().height || 0;
		const rowTopInContainer = rowRect.top - containerRect.top + scrollContainer.scrollTop;
		const rowGap = 8;
		const desiredScrollTop = rowTopInContainer - stickyHeaderHeight - rowGap;
		const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
		const clampedScrollTop = Math.max(0, Math.min(desiredScrollTop, maxScrollTop));
		const prefersReducedMotion = typeof window !== 'undefined'
			&& typeof window.matchMedia === 'function'
			&& window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		if (typeof scrollContainer.scrollTo === 'function') {
			scrollContainer.scrollTo({
				top: clampedScrollTop,
				behavior: prefersReducedMotion ? 'auto' : 'smooth',
			});
			return;
		}

		scrollContainer.scrollTop = clampedScrollTop;
	}, []);

	useEffect(() => {
		if (!expandedTaskId) {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			scrollExpandedTaskRowIntoView();
		}, 320);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [expandedTaskId, scrollExpandedTaskRowIntoView]);

	useEffect(() => {
		if (!expandedTaskId || isLoadingTaskResult) {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			scrollExpandedTaskRowIntoView();
		}, 100);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [expandedTaskId, isLoadingTaskResult, scrollExpandedTaskRowIntoView]);

	const clearExpandedDrawerData = useCallback(() => {
		setExpandedTaskResult(null);
		setExpandedTaskInput(null);
		setClosingTaskId(null);
	}, []);

	const closeExpandedDrawer = useCallback((taskId) => {
		if (expandedTaskId !== taskId) {
			return;
		}

		setClosingTaskId(taskId);
		setExpandedTaskId(null);
		setIsLoadingTaskResult(false);
	}, [expandedTaskId]);

	// Handler for row click to toggle inline task result drawer
	const handleRowClick = async (task) => {
		if (expandedTaskId === task.id) {
			closeExpandedDrawer(task.id);
		} else {
			// Open new drawer
			setClosingTaskId(null);
			setExpandedTaskId(task.id);
			setIsLoadingTaskResult(true);
			setExpandedTaskResult(null);
			setExpandedTaskInput(null);

			try {
				const resultsUrl = `/api/v1/tools/result/${task.id}/`;
				const inputsUrl = `/api/v1/tools/task/${task.id}/`;

				const [resultData, inputData] = await Promise.allSettled([
					request.get(resultsUrl),
					request.get(inputsUrl),
				]);

				if (resultData.status === 'fulfilled') {
					setExpandedTaskResult(resultData.value.data);
				}

				if (inputData.status === 'fulfilled') {
					setExpandedTaskInput(inputData.value.data);
				}
			} catch (e) {
				console.error("Error fetching task data:", e);
				toast.error("Failed to load task data");
			} finally {
				setIsLoadingTaskResult(false);
			}
		}
	};

	// Handle task deleted from drawer
	const handleTaskDeletedFromDrawer = () => {
		setExpandedTaskId(null);
		setExpandedTaskResult(null);
		setExpandedTaskInput(null);
		dispatch(getTasks());
	};

	// Handle task rerun from drawer
	const handleTaskRerunFromDrawer = () => {
		setExpandedTaskId(null);
		setExpandedTaskResult(null);
		setExpandedTaskInput(null);
		dispatch(getTasks());
	};

	const dialogContent = (() => {
		switch (dialogType) {
			case 'result':
				if (!currentResult)
					return (
						<Typography>There was a problem retrieving the data</Typography>
					)
				return (
					<Box>
						<Typography level='h6'>Here are the results of task number {currentTask?.id}</Typography>
						<Card sx={{ mt: 1 }}>
							<CardContent>
								{!!currentResult && !!currentResult.output.metadata &&
									<Box>
										{Object.entries(currentResult.output).map(([outputKey, outputValue]) => {
											if (isObject(outputValue)) return null
											return (
												<Box key={outputKey}>
													<Typography>
														<strong>{outputKey}:</strong> {outputValue}
													</Typography>
													<Divider sx={{ marginBlock: 1 }} />
												</Box>
											)
										})}
										<Typography mt={2} level='h5'>Metadata:</Typography>
									</Box>
								}
								<AccordionGroup sx={(theme) => ({
									[`& .${accordionSummaryClasses.indicator}`]: {
										transition: '0.2s',
									},
									[`& [aria-expanded="true"] .${accordionSummaryClasses.indicator}`]: {
										transform: 'rotate(45deg)',
									},
									[`& .${accordionSummaryClasses.button}`]: {
										bgcolor: 'lightgray',
									},
									[`& .${accordionSummaryClasses.button}:hover`]: {
										bgcolor: 'rgba(211,211,211,0.5)',
									},
									[`& .${accordionDetailsClasses.content}`]: {
										boxShadow: `inset 0 1px ${theme.vars.palette.divider}`,
										[`&.${accordionDetailsClasses.expanded}`]: {
											paddingBlock: '0.75rem',
										},
									}
								})} >
									<Accordion defaultExpanded={!currentResult.output.metadata}>
										<AccordionSummary indicator={<AddIcon />}>Exported fields</AccordionSummary>
										<AccordionDetails>
											{Object.entries(getMetadata(currentResult).documentFields).map(([fieldKey, fieldValue], index) => {
												return (
													<Box sx={{ mb: 1 }} key={fieldKey}>
														{
															// Handle primitive values (e.g., string, number, boolean)
															typeof fieldValue !== 'object' || fieldValue === null ? (
																<Typography textTransform='capitalize'>
																	<strong>{fieldKey}:</strong> {fieldValue}
																</Typography>
															) : Array.isArray(fieldValue) ? (
																// Handle array of values or objects
																<Typography textTransform='capitalize'>
																	<strong>{fieldKey}:</strong><br />
																	{fieldValue.map((item, index) => (
																		<Box key={index}>
																			{typeof item === 'object' && item !== null ? (
																				// Render nested object properties if item is an object
																				Object.entries(item).map(([subKey, subValue]) => (
																					<Typography key={subKey} sx={{ pl: 2 }}>
																						<strong>{subKey}:</strong> {subValue}<br />
																					</Typography>
																				))
																			) : (
																				// Render the value directly if it's not an object
																				<Typography sx={{ pl: 4 }}>
																					{item}
																				</Typography>
																			)}
																			<hr />
																		</Box>
																	))}
																</Typography>
															) : (
																// Handle plain object values
																<Typography textTransform='capitalize'>
																	<strong>{fieldKey}:</strong><br />
																	<pre>{JSON.stringify(fieldValue, null, 2)}</pre>
																	{/* {Object.entries(fieldValue).map(([subKey, subValue]) => (
																		<Typography key={subKey} sx={{ pl: 2 }}>
																			<strong>{subKey}:</strong> {subValue}<br />
																		</Typography>
																	))} */}
																</Typography>
															)
														}
														<Typography>
															<strong>Confidence:</strong> {getMetadata(currentResult).extractionStats.documentFields[fieldKey]}
														</Typography>
														{index < Object.entries(getMetadata(currentResult).documentFields).length - 1 && <Divider sx={{ mt: 1 }} />}
													</Box>
												)
											})}
										</AccordionDetails>
									</Accordion>
									<Accordion>
										<AccordionSummary indicator={<AddIcon />}>Validations</AccordionSummary>
										<AccordionDetails>
											{getMetadata(currentResult).validation.map((validationField, index) => {
												return (
													<Card sx={{ mb: 2 }} key={index}>
														<CardContent>
															<Typography>
																<strong>Key:</strong> {validationField.fieldKey}
																<span
																	style={{
																		border: `2px solid ${validationField.isValid ? 'green' : 'red'}`,
																		padding: '4px',
																		borderRadius: '4px',
																		marginInlineStart: '5px'
																	}}>{validationField.isValid ? 'Valid' : 'Not Valid'}</span>
															</Typography>

															<Typography>
																<strong>Reason:</strong> <pre>{JSON.stringify(validationField.reason, null, 2)}</pre>
															</Typography>
															<Typography>
																<strong>Confidence:</strong> {getMetadata(currentResult).extractionStats.validation[validationField.fieldKey]}
															</Typography>

														</CardContent>
													</Card>
												)
											})}
										</AccordionDetails>
									</Accordion>

								</AccordionGroup>

							</CardContent>
						</Card>
					</Box >
				)
			case 'input':
				return (
					<Box>
						<Typography level='h6'>This is the input parameters of task number {currentTask?.id}</Typography>
						<Card sx={{ mt: 1 }}>
							<CardContent>
								<Typography><strong>Policy:</strong> {policies.find(p => p.id === currentTask.payload.policy_id)?.name || ''}</Typography>
								{!!currentTask?.payload && !!currentTask?.payload?.policy_output_id && <Typography><strong>Output Policy:</strong> {policies.find(p => p.id === currentTask?.payload?.policy_output_id)?.name || ''}</Typography>}
								<Divider sx={{ marginBlock: 1 }} />
								<Typography level='h5'><strong>Reference Data:</strong></Typography>
								{!!currentTask?.payload && Object.entries(currentTask?.payload?.objectReference).map(([refKey, refValue]) => (
									<Box key={refKey}>
										<Typography>&nbsp;&nbsp;&nbsp;&nbsp;<strong>{refKey}:</strong> {refValue}</Typography>
									</Box>
								))}
								<Divider sx={{ marginBlock: 1 }} />
								<Button variant='plain' onClick={onOpenDocument}>Show document</Button>
							</CardContent>
						</Card>
					</Box>
				)
			case 'logs':
				return (
					<Box>
						<Typography level='h6'>Here are the logs of task number {currentTask?.id}</Typography>
						<Card sx={{ maxHeight: '80%', overflowY: 'auto', backgroundColor: 'black', mt: 1 }}>
							<CardContent>
								{currentTask.events.map((event, index) => (
									<Typography key={index} sx={{ color: event.level === 'error' ? 'red' : 'whitesmoke' }}>{event.timestamp} - {event.level} - {event.log}</Typography>
								))}
							</CardContent>
						</Card>
					</Box>
				)
				default:
					return <></>
		}
	})();

	const onDialogClose = () => {
		setDialogType("")
		setCurrentTask(null)
		setCurrentResult(null)
	}

	const openResultAfterRedirect = useCallback(() => {
		if (tasks.length === 0) return
		const queryParams = new URLSearchParams(window.location.search)
		const task_id = queryParams.get('task_id')
		if (!task_id) return
		const task = tasks.find(t => `${t.id}` === task_id)
		if (!task) return
		const status = queryParams.get('status')
		onOpenDialog(status === "completed" ? 'result' : 'logs', task)
	}, [onOpenDialog, tasks]);

	useEffect(() => {
		dispatch(getPolicies());
	}, [dispatch]);

	useEffect(() => {
		openResultAfterRedirect()
	}, [openResultAfterRedirect])

	useEffect(() => {
		request.get("api/v1/tools/task-statuses/")
			.then((response) => {
				setStatuses(response.data);
			})
			.catch((error) => {
				console.error("Failed to fetch task statuses:", error);
			});
	}, []);


	const handleRerun = async (taskIds) => {
		if (!taskIds || taskIds.length === 0) {
			console.warn('No tasks selected to rerun');
			alert('No tasks selected to rerun');
			return;
		}

		setRerunningTasks((prev) => new Set([...prev, ...taskIds]));

		try {
			for (const taskId of taskIds) {
				const task = tasks.find((t) => t.id === taskId);
				if (!task) continue;

				// Fetch the task data to get the required body for the update
				await request.post(`/api/v2/tools/task/${task.id}/rerun/`);
				// const taskPayload = response.data.payload;

				// const policy = policies.find(p => p.id === taskPayload.policy_id);
				// if (policy && !policy.settings?.tool) {
				//   toast.warn("Running task on default tool - no tool selected in policy settings.");
				// }

				// // Update the current task to trigger rerun
				// await request.put(`/api/v1/tools/task/${taskId}/`, {
				//   status: 'queued for rerun',
				//   type: 'compliance',
				//   payload: {
				// 	// original_task_id: task.id,
				// 	policy_id: taskPayload.policy_id,
				// 	objectReference: taskPayload.objectReference,
				// 	document: {
				// 	  type: taskPayload.document.type,
				// 	  base64: taskPayload.document.base64,
				// 	},
				// 	...(taskPayload.policy_output_id && { policy_output_id: taskPayload.policy_output_id }),
				//   },
				// });
			}

			dispatch(getTasks());
			setSelectedTaskIds([]);
		} catch (error) {
			alert('There was an issue rerunning the tasks.');
		} finally {
			setRerunningTasks((prev) => {
				const newSet = new Set(prev);
				taskIds.forEach((id) => newSet.delete(id));
				return newSet;
			});
		}
	};

	const handleReplayClick = async (task) => {
		await handleRerun([task.id]);
	};

	const handleRerunSelected = async () => {
		await handleRerun(selectedTaskIds);
	};

	const handleCheckboxChange = (taskId) => {
		setSelectedTaskIds((prev) =>
			prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
		);
	};

	const handleSelectAll = (event) => {
		if (event.target.checked) {
			setSelectedTaskIds(tasks.map((task) => task.id));
		} else {
			setSelectedTaskIds([]);
		}
	};

	const handleClearSearch = () => {
		dispatch(setSearch(''));
		dispatch(getTasks());
	};

	const formatExecutionTimeNatural = (log) => {
		const timeRegex = /([a-zA-Z\s]*time[:]?)[^\d]*(\d+(\.\d+)?)\s*seconds?/i;
		const match = log.match(timeRegex);

		if (match) {
			const label = match[1].trim();
			const rawSeconds = parseFloat(match[2]);

			if (rawSeconds < 1) {
				const ms = Math.round(rawSeconds * 1000);
				return log.replace(timeRegex, `${label} ${ms} milliseconds`);
			}

			const hours = Math.floor(rawSeconds / 3600);
			const minutes = Math.floor((rawSeconds % 3600) / 60);
			const seconds = Math.floor(rawSeconds % 60);

			let parts = [];
			if (hours) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
			if (minutes) parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
			if (seconds || parts.length === 0) parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);

			const formatted = parts.join(' ');

			return log.replace(timeRegex, `${label} ${formatted}`);
		}

		return log;
	};

	const [tasksToArchive, setTasksToArchive] = useState([]);
	const [isArchiving, setIsArchiving] = useState(false);

	const handleArchiveTasks = async () => {
		setIsArchiving(true);
		try {
			await Promise.all(
				tasksToArchive.map((taskId) =>
					request.delete(`/api/v1/tools/task/${taskId}/`)
				)
			);
			dispatch(getTasks()); // Refresh the list
			setSelectedTaskIds([]); // Clear selection
		} catch (e) {
			console.error("Error archiving tasks:", e);
			alert("Failed to archive one or more tasks.");
		} finally {
			setIsArchiving(false);
			setTasksToArchive([]); // Reset modal
		}
	};

	const handleFilterChange = (changes) => {
		dispatch(setFilters(changes));
		dispatch(getTasks());
	};

	useEffect(() => {
		dispatch(getCompletedTasksToday());
	}, [dispatch]);

	// Close drawer and clear data when pagination changes
	useEffect(() => {
		setExpandedTaskId(null);
		setExpandedTaskResult(null);
		setExpandedTaskInput(null);
	}, [pagination.page, pagination.page_size, pagination.search, pagination.ordering]);


	return (
		<>
			<style>{pulseAnimation}</style>
			<CreateTaskDrawer
				loading={loading}
				open={startTask}
				onClose={() => setStartTask(false)}
				refreshTable={refreshTable}
				currentPolicy={currentPolicy}
				setCurrentPolicy={setCurrentPolicy}
				currentOutputPolicy={currentOutputPolicy}
				setCurrentOutputPolicy={setCurrentOutputPolicy}
				selectedFiles={selectedFiles}
				setSelectedFiles={setSelectedFiles}
				handleFileChange={handleFileChange}
				handleRemoveFile={handleRemoveFile}
				handleSubmit={handleSubmit}
				referencedObjects={referencedObjects}
				setReferencedObjects={setReferencedObjects}
				policies={policies}
				pages={pages}
				currentPage={currentPage}
				setCurrentPage={setCurrentPage}
			/>
			<Modal open={!!dialogType} onClose={onDialogClose}>
				<ModalDialog size='lg' >
					<Tooltip title="Close">
						<ModalClose />
					</Tooltip>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', position: 'absolute', right: 43, top: 10 }}>
						{dialogType !== 'logs' && <Tooltip title="Logs">
							<IconButton onClick={() => onOpenDialog('logs', currentTask)}>
								<ReorderOutlinedIcon />
							</IconButton>
						</Tooltip>}
						{dialogType !== 'input' && <Tooltip title="Input">
							<IconButton onClick={() => onOpenDialog('input', currentTask)}>
								<InputIcon />
							</IconButton>
						</Tooltip>}
						{dialogType !== 'result' && currentTask?.status === 'completed' && <Tooltip title="Result">
							<IconButton onClick={() => onOpenDialog('result', currentTask)}>
								<OutputIcon />
							</IconButton>
						</Tooltip>}
					</Box>
					<DialogTitle>Task {dialogType}</DialogTitle>
					<DialogContent>
						{dialogContent}
					</DialogContent>

				</ModalDialog>
			</Modal>
			<Modal open={open} onClose={() => setOpen(false)}>
				<ModalDialog layout="center">
					<ModalClose onClick={() => setOpen(false)} />
					<DialogTitle>Event Logs</DialogTitle>
					<DialogContent>
						<List
							sx={{
								mx: 'calc(-1 * var(--ModalDialog-padding))',
								px: 'var(--ModalDialog-padding)',
								maxHeight: 400,
								overflowY: 'auto',
								pr: 1,
								position: 'relative',
								scrollbarWidth: 'thin',
								'&::-webkit-scrollbar': {
									width: '6px',
								},
								'&::-webkit-scrollbar-track': {
									background: 'transparent',
								},
								'&::-webkit-scrollbar-thumb': {
									background: 'rgba(150, 150, 150, 0.6)',
									borderRadius: '6px',
									transition: 'background 0.3s ease-in-out, opacity 0.3s',
									opacity: 0,
								},
								'&:hover::-webkit-scrollbar-thumb': {
									opacity: 1,
								},
								'&::-webkit-scrollbar-thumb:hover': {
									background: 'rgba(150, 150, 150, 0.9)',
								},
							}}
						>
							{input?.events?.length > 0 ? (
								input.events.map((event, index) => (
									<ListItem key={index}>
										<Stack spacing={0.5} sx={{ p: 1, borderRadius: '8px', bgcolor: 'background.level1', width: '100%' }}>
											<Typography level="body2">
												<strong>[{formatTimestamp(event.timestamp)}]</strong>
											</Typography>
											<Stack direction="row" spacing={1} alignItems="center">
												<Chip size="sm" color={event.level === 'error' ? 'danger' : event.level === 'warning' ? 'warning' : 'success'}>
													{event.level.toUpperCase()}
												</Chip>
												<Typography level="body2">{formatExecutionTimeNatural(event.log)}</Typography>
											</Stack>
										</Stack>
									</ListItem>
								))
							) : (
								<Typography level="body2" sx={{ textAlign: 'center', color: 'neutral' }}>
									No logs available.
								</Typography>
							)}
						</List>
					</DialogContent>
				</ModalDialog>
			</Modal>
			<Modal open={!!taskToDelete} onClose={() => setTaskToDelete(null)}>
				<ModalDialog>
					<ModalClose />
					<DialogTitle>Archive Task</DialogTitle>
					<DialogContent>
						Are you sure you want to archive task {taskToDelete?.id}?
					</DialogContent>
					<DialogActions>
						<Button color="danger" onClick={handleDeleteTask}>Archive</Button>
						<Button variant="plain" onClick={() => setTaskToDelete(null)}>Cancel</Button>
					</DialogActions>
				</ModalDialog>
			</Modal>

			<Modal open={tasksToArchive.length > 0} onClose={() => setTasksToArchive([])}>
				<ModalDialog>
					<ModalClose />
					<DialogTitle>Archive Tasks</DialogTitle>
					<DialogContent>
						Are you sure you want to archive {tasksToArchive.length} task{tasksToArchive.length > 1 ? 's' : ''}?
					</DialogContent>
					<DialogActions>
						<Button
							color="danger"
							onClick={handleArchiveTasks}
							loading={isArchiving}
							disabled={isArchiving}
						>
							Archive
						</Button>
						<Button
							variant="plain"
							onClick={() => setTasksToArchive([])}
							disabled={isArchiving}
						>
							Cancel
						</Button>
					</DialogActions>
				</ModalDialog>
			</Modal>


			<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
				<Stack
				direction={{ xs: 'column', sm: 'row' }}
				alignItems="center"
				spacing={1}
				sx={{ mb: 2, mt: 2, flexShrink: 0 }}
			>
				<Input
					placeholder="Search"
					value={pagination.search}
					endDecorator={
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							{pagination.search && (
								<IconButton
									onClick={handleClearSearch}
									variant="plain"
									disabled={isTasksLoading}
									size="sm"
								>
									<ClearIcon />
								</IconButton>
							)}
							<IconButton
								onClick={() => {
									// Close all filter popovers when searching
									setFilterStates(prev => {
										const newState = {};
										for (const k in prev) {
											newState[k] = { ...prev[k], open: false, anchorEl: null };
										}
										return newState;
									});
									dispatch(getTasks());
								}}
								variant="plain"
								disabled={isTasksLoading}
							>
								<SearchIcon />
							</IconButton>
						</Box>
					}
					onChange={e => {
						dispatch(setSearch(e.target.value));
						dispatch(setPagination({ page: 1 }));
					}}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							// Close all filter popovers when searching with Enter
							setFilterStates(prev => {
								const newState = {};
								for (const k in prev) {
									newState[k] = { ...prev[k], open: false, anchorEl: null };
								}
								return newState;
							});
							dispatch(getTasks());
						}
					}}
					sx={{ flexGrow: 1 }}
					disabled={isTasksLoading}
				/>

				<IconButton
					onClick={() => {
						// Close all filter popovers when refreshing
						setFilterStates(prev => {
							const newState = {};
							for (const k in prev) {
								newState[k] = { ...prev[k], open: false, anchorEl: null };
							}
							return newState;
						});
						dispatch(getTasks());
					}}
					sx={{ p: 1 }}
					disabled={isTasksLoading}
				>
					<RefreshIcon />
					<Typography sx={{ ml: 1 }}>Refresh</Typography>
				</IconButton>

				<Button
					disabled={isPoliciesLoading || selectedTaskIds.length === 0 || rerunningTasks.size > 0 || isTasksLoading}
					startDecorator={rerunningTasks.size > 0 ? <CircularProgress size="sm" /> : <ReplayIcon />}
					color="success"
					onClick={handleRerunSelected}
					sx={{ whiteSpace: 'nowrap' }}
				>
					{rerunningTasks.size > 0 ? 'Re-running Tasks...' : 'Re-run Tasks'}
				</Button>

				<Button
					disabled={isPoliciesLoading || selectedTaskIds.length === 0 || isTasksLoading}
					startDecorator={<ArchiveOutlinedIcon />}
					color="danger"
					onClick={() => setTasksToArchive(selectedTaskIds)}
					sx={{ whiteSpace: 'nowrap' }}
				>
					Archive Tasks
				</Button>

				<Button
					disabled={isPoliciesLoading && pages.length > 0}
					startDecorator={<AddRoundedIcon />}
					onClick={() => setStartTask(true)}
					sx={{ whiteSpace: 'nowrap' }}
				>
					Start New Tasks
				</Button>
				</Stack>

				<Sheet ref={tableScrollContainerRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto', overflowX: 'hidden', position: 'relative' }}>
				{isTasksLoading ? (
					<Table
						variant='outlined'
						aria-label="loading table"
						stickyHeader
						stickyFooter
						sx={{
							'--TableCell-headBackground': 'var(--joy-palette-background-level1)',
							'--Table-headerUnderlineThickness': '1px',
							'--TableCell-paddingY': '4px',
							'--TableCell-paddingX': '8px',
							width: '100%',
							tableLayout: 'fixed',
							'& thead th:nth-child(1)': { width: '5%' },
							'& thead th:nth-child(2)': { width: '5%' },
							'& thead th:nth-child(3)': { width: '17%' },
							'& thead th:nth-child(4)': { width: '17%' },
							'& thead th:nth-child(5)': { width: '10%' },
							'& thead th:nth-child(6)': { width: '17%' },
							'& thead th:nth-child(7)': { width: '17%' },
							'& thead th:nth-child(8)': { width: '12%' },
						}}
					>
						<thead>
							<tr>
								<th>
									<Tooltip
										title={
											selectedTaskIds.length === tasks.length
												? 'Deselect all tasks'
												: 'Select all tasks'
										}
									>
										<Checkbox
											checked={selectedTaskIds.length > 0 && selectedTaskIds.length === tasks.length}
											indeterminate={selectedTaskIds.length > 0 && selectedTaskIds.length < tasks.length}
											onChange={handleSelectAll}
											sx={{ marginLeft: "10px" }}
											disabled={true}
										/>
									</Tooltip>
								</th>
								{tableHeaders.map(header => (
									<th key={header.key}>
										<Link
											underline="none"
											color="dark"
											component="button"
											disabled={true}
											sx={{
												fontWeight: 'lg',
											}}
										>
											{header.name}
										</Link>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{Array.from({ length: 20 }).map((_, index) => (
								<tr key={index}>
									<td>
										<Box sx={{ ...skeletonBoxSx, width: 20, height: 20 }} />
									</td>
									<td>
										<Box sx={{ ...skeletonBoxSx, width: '60%', height: 16 }} />
									</td>
									<td>
										<Box sx={{ ...skeletonBoxSx, width: '70%', height: 24 }} />
									</td>
									<td>
										<Box sx={{ ...skeletonBoxSx, width: '80%', height: 16 }} />
									</td>
									<td>
										<Box sx={{ ...skeletonBoxSx, width: '60%', height: 16 }} />
									</td>
									<td>
										<Box sx={{ ...skeletonBoxSx, width: '90%', height: 16 }} />
									</td>
									<td>
										<Box sx={{ ...skeletonBoxSx, width: '90%', height: 16 }} />
									</td>
									<td>
										<Box sx={{ display: 'flex', gap: 1 }}>
											<Box sx={{ ...skeletonBoxSx, width: 32, height: 32 }} />
											<Box sx={{ ...skeletonBoxSx, width: 32, height: 32 }} />
											<Box sx={{ ...skeletonBoxSx, width: 32, height: 32 }} />
										</Box>
									</td>
								</tr>
							))}
						</tbody>
						<tfoot>
							<tr>
								<td colSpan={8}>
									<Grid container alignItems="center" justifyContent="space-between">
										<Grid item>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
												<Box sx={{ ...skeletonBoxSx, width: 80, height: 32 }} />
												<Box sx={{ ...skeletonBoxSx, width: 150, height: 20 }} />
											</Box>
										</Grid>
										<Grid item>
											<Grid container spacing={2} alignItems="center">
												<Grid item>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
														<Box sx={{ ...skeletonBoxSx, width: 80, height: 20 }} />
														<Box sx={{ ...skeletonBoxSx, width: 60, height: 32 }} />
													</Box>
												</Grid>
												<Grid item>
													<Box sx={{ ...skeletonBoxSx, width: 80, height: 20 }} />
												</Grid>
												<Grid item>
													<Box sx={{ display: 'flex', gap: 1 }}>
														<Box sx={{ ...skeletonBoxSx, width: 32, height: 32 }} />
														<Box sx={{ ...skeletonBoxSx, width: 32, height: 32 }} />
													</Box>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</td>
							</tr>
						</tfoot>
					</Table>
				) : (!isTasksLoading && hasFetchedTasks && tasks.length === 0) ? (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							height: '400px',
							width: '100%',
							gap: 2,
						}}
					>
						<Box
							sx={{
								width: 80,
								height: 80,
								borderRadius: '50%',
								bgcolor: 'background.level2',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<ArchiveOutlinedIcon sx={{ fontSize: 40, color: 'neutral.500' }} />
						</Box>
						<Typography level="h4" color="neutral">
							{pagination.search || Object.keys(filters || {}).length > 0 ? 'No tasks found' : 'No tasks available'}
						</Typography>
						<Typography level="body-lg" color="neutral.500" textAlign="center">
							{pagination.search
								? `No tasks match your search "${pagination.search}". Try adjusting your search terms.`
								: Object.keys(filters || {}).length > 0
									? 'No tasks match your current filters. Try adjusting or clearing your filters.'
									: 'Get started by creating your first task using the "Start New Tasks" button above.'
							}
						</Typography>
						<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
							{pagination.search && (
								<Button
									variant="outlined"
									onClick={() => {
										dispatch(setSearch(''));
										dispatch(getTasks());
									}}
									sx={{ mt: 1 }}
								>
									Clear Search
								</Button>
							)}
							{Object.keys(filters || {}).length > 0 && (
								<Button
									variant="outlined"
									color="neutral"
									onClick={() => {
										// Clear all filters by setting each existing filter key to empty/null
										const clearedFilters = {};
										Object.keys(filters).forEach(key => {
											clearedFilters[key] = '';
										});
										dispatch(setFilters(clearedFilters));

										// Also reset filter states in the UI
										setFilterStates(prev => {
											const newState = {};
											for (const k in prev) {
												newState[k] = {
													...prev[k],
													open: false,
													anchorEl: null,
													value: '',
													op: '',
													val: '',
													unit: 'seconds'
												};
											}
											return newState;
										});

										dispatch(getTasks());
									}}
									sx={{ mt: 1 }}
								>
									Clear Filters
								</Button>
							)}
						</Box>
					</Box>
				) : (
					<Table
						variant='outlined'
						aria-label="table with sticky header"
						stickyHeader
						stickyFooter
						hoverRow
						sx={{
							'--TableCell-headBackground': 'var(--joy-palette-background-level1)',
							'--Table-headerUnderlineThickness': '1px',
							'--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
							'--TableCell-paddingY': '4px',
							'--TableCell-paddingX': '8px',
							width: '100%',
							tableLayout: 'fixed',
							'& tbody tr[data-inline-drawer-row="true"]': {
								background: 'transparent',
							},
							'& tbody tr[data-inline-drawer-row="true"]:hover': {
								background: 'transparent',
							},
							'& tbody tr[data-inline-drawer-row="true"] > td[data-inline-drawer-cell="true"]': {
								padding: 0,
								height: 0,
								lineHeight: 0,
								borderBottom: 'none',
								background: 'transparent',
							},
							'& thead th:nth-child(1)': { width: '5%' },
							'& thead th:nth-child(2)': { width: '7%' },
							'& thead th:nth-child(3)': { width: '15%' },
							'& thead th:nth-child(4)': { width: '17%' },
							'& thead th:nth-child(5)': { width: '10%' },
							'& thead th:nth-child(6)': { width: '17%' },
							'& thead th:nth-child(7)': { width: '17%' },
							'& thead th:nth-child(8)': { width: '12%' },
						}}
					>
						<thead>
							<tr>
								<th>
									<Tooltip
										title={
											selectedTaskIds.length === tasks.length
												? 'Deselect all tasks'
												: 'Select all tasks'
										}
									>
										<Checkbox
											checked={selectedTaskIds.length > 0 && selectedTaskIds.length === tasks.length}
											indeterminate={selectedTaskIds.length > 0 && selectedTaskIds.length < tasks.length}
											onChange={handleSelectAll}
											sx={{ marginLeft: "10px" }}
											disabled={isTasksLoading}
										/>
									</Tooltip>
								</th>
								{tableHeaders.map(header => {
									const { key, name, filterLabel, filterType } = header;
									const { open, anchorEl, value } = filterStates[key] || {};

									return (
										<th key={key}>
											{key !== 'actions' && (
												<>
													<Box
														sx={{
															display: 'flex',
															alignItems: 'center',
															gap: 1,
														}}
													>
														<Box
															onClick={() => handleOrder(key)}
															disabled={isTasksLoading}
															sx={{
																display: 'flex',
																alignItems: 'center',
																cursor: 'pointer',
															}}
														>
															<span>{name}</span>
															<ArrowDownwardIcon
																sx={{
																	ml: 0.5,
																	fontSize: 18,
																	opacity: key === pagination.ordering ? 1 : 0,
																	transform: pagination.isDescending ? 'rotate(0deg)' : 'rotate(180deg)',
																	color: 'text.secondary',
																	'&:hover': { color: 'primary.main' },
																}}
															/>
														</Box>

														<FilterAltIcon
															onClick={(e) => handlePopperToggle(key, e)}
															sx={{
																fontSize: 20,
																cursor: 'pointer',
																color:
																	open || isFilterActive(key, tableHeaders.find(h => h.key === key)?.filterType)
																		? 'primary.main'
																		: 'text.secondary',
																'&:hover': { color: 'primary.main' },
															}}
														/>

													</Box>

													{Boolean(anchorEl) && open && (
														<ClickAwayListener onClickAway={() => {
															setFilterStates(prev => ({
																...prev,
																[key]: {
																	...prev[key],
																	open: false,
																	anchorEl: null,
																},
															}));
														}}>
															<Popper
																id={`filter-popper-${key}`}
																open={open}
																anchorEl={anchorEl}
																transition
																placement="bottom-end"
																sx={{ zIndex: 1500 }}
															>
																{({ TransitionProps }) => (
																	<Fade {...TransitionProps} timeout={350}>
																		<Sheet
																			variant="outlined"
																			sx={{
																				p: 2,
																				minWidth: 250,
																				boxShadow: 'lg',
																				borderRadius: 'md',
																				display: 'flex',
																				flexDirection: 'column',
																				gap: 1.5,
																			}}
																		>
																			<Typography level="title-sm">
																				Filter by {filterLabel || name}
																			</Typography>

																			{filterType === "date" && (
																				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
																					<Select
																						value={filterStates[key]?.op || ''}
																						onChange={(e, newValue) => handleInputChange(key, newValue, 'op')}
																						size="sm"
																						placeholder="Select operator"
																						slotProps={{
																							listbox: {
																								sx: { zIndex: 1600 },
																							},
																						}}
																					>
																						<Option value="gt">Greater than</Option>
																						<Option value="lt">Less than</Option>
																						<Option value="eq">Equal to</Option>
																					</Select>

																					<DatePicker
																						value={
																							filterStates[key]?.val && dayjs(filterStates[key]?.val).isValid()
																								? dayjs(filterStates[key]?.val)
																								: null
																						}
																						onChange={(newValue) => {
																							const formattedDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : null;
																							handleInputChange(key, formattedDate, 'val');
																						}}
																						slotProps={{
																							textField: {
																								size: "small",
																								placeholder: `Select ${filterLabel || name}`,
																								variant: "outlined",
																							},
																							field: { clearable: true },
																							popper: {
																								modifiers: [
																									{
																										name: "zIndex",
																										enabled: true,
																										phase: "write",
																										fn: ({ state }) => {
																											state.styles.popper.zIndex = "2000";
																										},
																									},
																								],
																								sx: {
																									zIndex: 2000,
																								},
																							},
																						}}
																					/>
																				</Box>
																			)}

																			{filterType === 'duration' && (
																				<Box
																					sx={{
																						display: 'flex',
																						flexDirection: 'column',
																						gap: 2,
																					}}
																				>
																					<FormControl size="sm">
																						<FormLabel>Operator</FormLabel>
																						<Select
																							value={filterStates[key]?.op || ''}
																							onChange={(e, newValue) => handleInputChange(key, newValue, 'op')}
																							placeholder="Select operator"
																							slotProps={{
																								listbox: { sx: { zIndex: 1600 } },
																							}}
																						>
																							<Option value="gt">Greater than</Option>
																							<Option value="lt">Less than</Option>
																							<Option value="eq">Equal to</Option>
																						</Select>
																					</FormControl>

																					<FormControl size="sm">
																						<FormLabel>{filterLabel || name}</FormLabel>
																						<Input
																							type="number"
																							value={filterStates[key]?.val || ''}
																							onChange={(e) => {
																								const inputValue = e.target.value;
																								// Only allow numbers, decimal points, and empty string
																								if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
																									handleInputChange(key, inputValue, 'val');
																								}
																							}}
																							placeholder={`Enter ${filterLabel || name}`}
																						/>
																					</FormControl>

																					<FormControl size="sm">
																						<FormLabel>Unit</FormLabel>
																						<Select
																							value={filterStates[key]?.unit || 'seconds'}
																							onChange={(e, newValue) => handleInputChange(key, newValue, 'unit')}
																							slotProps={{
																								listbox: {
																									sx: { zIndex: 1600 },
																								},
																							}}
																						>
																							<Option value="seconds">Seconds</Option>
																							<Option value="minutes">Minutes</Option>
																							<Option value="hours">Hours</Option>
																						</Select>
																					</FormControl>
																				</Box>
																			)}

																			{filterType === 'policy' && (
																				<Select
																					value={value || ''}
																					onChange={(e, newValue) => handleInputChange(key, newValue)}
																					size="sm"
																					placeholder={`Select ${filterLabel || name}`}
																					slotProps={{
																						listbox: {
																							sx: {
																								zIndex: 1600, // Ensure above Popper (1500)
																							},
																						},
																					}}
																				>
																					{policies.map((policy) => (
																						<Option key={policy.id} value={policy.name}>
																							{policy.name}
																						</Option>
																					))}
																				</Select>
																			)}

																			{filterType === 'text' && (
																				<Input
																					value={value}
																					onChange={(e) => handleInputChange(key, e.target.value)}
																					placeholder={`Enter ${filterLabel || name}`}
																					size="sm"
																				/>
																			)}

																			{filterType === 'number' && (
																				<Input
																					type="number"
																					value={value}
																					onChange={(e) => {
																						const inputValue = e.target.value;
																						// Only allow numbers and empty string
																						if (inputValue === '' || /^\d+$/.test(inputValue)) {
																							handleInputChange(key, inputValue);
																						}
																					}}
																					placeholder={`Enter ${filterLabel || name}`}
																					size="sm"
																				/>
																			)}

																			{filterType === 'status' && (
																				<Select
																					value={value || ''}
																					onChange={(e, newValue) => handleInputChange(key, newValue)}
																					size="sm"
																					placeholder={`Select ${filterLabel || name}`}
																					slotProps={{
																						listbox: {
																							sx: {
																								zIndex: 1600,
																							},
																						},
																					}}
																				>
																					{statuses.map(({ value, label }) => (
																						<Option key={value} value={value}>
																							{label}
																						</Option>
																					))}
																				</Select>
																			)}

																			<Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
																				<Button
																					onClick={() => handleApply(key)}
																					size="sm"
																					color="primary"
																					variant="solid"
																					disabled={isApplyDisabled(key, filterType)}
																					sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
																				>
																					<CheckIcon fontSize="small" />
																					Apply
																				</Button>
																				<Button
																					onClick={() => handleClear(key, filterType)}
																					size="sm"
																					color="neutral"
																					variant="outlined"
																					sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
																				>
																					<ClearIcon fontSize="small" />
																					Clear
																				</Button>
																			</Box>
																		</Sheet>
																	</Fade>
																)}
															</Popper>
														</ClickAwayListener>

													)}



												</>
											)}
										</th>
									);
								})}
							</tr>
						</thead>

						<tbody>
							{tasks.map((task) => (
								<Fragment key={task.id}>
									<tr 
										ref={expandedTaskId === task.id ? expandedTaskRowRef : null}
										onClick={() => handleRowClick(task)}
										style={{ 
											cursor: 'pointer',
											backgroundColor: (expandedTaskId === task.id || closingTaskId === task.id)
												? 'var(--joy-palette-background-level1)'
												: undefined,
										}}
									>
										<td onClick={(e) => e.stopPropagation()}>
											<Checkbox
												checked={selectedTaskIds.includes(task.id)}
												onChange={() => handleCheckboxChange(task.id)}
												sx={{ marginLeft: "10px" }}
												disabled={isTasksLoading}
											/>
										</td>
										<td>
											<Typography level="body-xs" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
												{task.id}
											</Typography>
										</td>
										<td>
											<Typography level="body-xs" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
												<Chip
													color={taskStatus[task.status.toLowerCase()] || 'neutral'}
													size="sm"
												>
													{task.status.toUpperCase()}
												</Chip>
											</Typography>
										</td>
										<td>
											<Typography level="body-xs" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
												{task.policy_name} | {task.type}
											</Typography>
											</td>
											<td>
												{task.duration !== null && task.duration !== undefined && calculateDuration(Number(task.duration))}
											</td>
											<td>
											<Typography level="body-xs" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
												{new Date(task.created_at).toLocaleString('en-IL', {
													year: 'numeric',
													month: 'short',
													day: '2-digit',
													hour: '2-digit',
													minute: '2-digit',
													hour12: false,
												})}
											</Typography>
										</td>
										<td>
											<Typography level="body-xs" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
												{(task.status === 'completed' || task.status === 'failed') && new Date(task.last_modified).toLocaleString('en-IL', {
													year: 'numeric',
													month: 'short',
													day: '2-digit',
													hour: '2-digit',
													minute: '2-digit',
													hour12: false,
												})}
											</Typography>
										</td>
										<td onClick={(e) => e.stopPropagation()}>
											<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
												<IconButton
													variant="plain"
													onClick={() => onOpenDialog(task.status === 'Created' ? 'logs' : 'result', task)}
													aria-label={`Show ${task.status === 'Created' ? 'logs' : 'result'}`}
													disabled={isTasksLoading}
												>
													<VisibilityIcon />
												</IconButton>
												<IconButton onClick={() => handleReplayClick(task)} disabled={isTasksLoading}>
													{rerunningTasks.has(task.id) ? (<CircularProgress color="neutral" />) : (<ReplayIcon />)}
												</IconButton>
												<IconButton onClick={() => setTaskToDelete(task)} disabled={isTasksLoading}>
													<ArchiveOutlinedIcon />
												</IconButton>
											</Box>
										</td>
									</tr>
									{(expandedTaskId === task.id || closingTaskId === task.id) && (
										<tr data-inline-drawer-row="true" data-testid="inline-drawer-row">
											<td
												colSpan={8}
												data-inline-drawer-cell="true"
												data-testid="inline-drawer-host-cell"
												style={{
													padding: 0,
													height: 0,
													lineHeight: 0,
													borderBottom: 'none',
													background: 'transparent',
												}}
											>
												<TaskResultDrawer
													taskId={task.id}
													result={expandedTaskResult}
													input={expandedTaskInput}
													isLoading={isLoadingTaskResult}
													isOpen={expandedTaskId === task.id}
													onClose={() => closeExpandedDrawer(task.id)}
													onExited={closingTaskId === task.id ? clearExpandedDrawerData : undefined}
													onTaskDeleted={handleTaskDeletedFromDrawer}
													onTaskRerun={handleTaskRerunFromDrawer}
												/>
											</td>
										</tr>
									)}
								</Fragment>
							))}
						</tbody>

						<tfoot>
							<tr>
								<td colSpan={8}>
									<Grid container alignItems="center" justifyContent="space-between">
										{/* Left side: Task metrics */}
										<Grid item>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<Chip
														variant="soft"
														color="success"
														size="lg"
														sx={{
															'--Chip-minHeight': '32px',
															'--Chip-paddingInline': '12px'
														}}
													>
														{isCompletedTasksLoading ? (
															<CircularProgress size="sm" color="neutral" />
														) : (
															completedTasksToday
														)}
													</Chip>
													<Typography level="title-lg">
														Tasks Completed Today
													</Typography>
												</Box>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<Chip
														variant="soft"
														color="primary"
														size="lg"
														sx={{
															'--Chip-minHeight': '32px',
															'--Chip-paddingInline': '12px'
														}}
													>
														{isTasksLoading ? (
															<CircularProgress size="sm" color="neutral" />
														) : (
															pagination.count?.toLocaleString() || '0'
														)}
													</Chip>
													<Typography level="title-lg">
														Total Tasks
													</Typography>
												</Box>
											</Box>
										</Grid>
										{/* Right side: Pagination controls */}
										<Grid item>
											<Grid container spacing={2} alignItems="center">
												<Grid item>
													<FormControl orientation="horizontal" size="sm">
														<FormLabel>Rows per page:</FormLabel>
														<Select value={pagination.page_size} onChange={(_, value) => handlePageSize(value)} disabled={isTasksLoading}>
															<Option value={10}>10</Option>
															<Option value={25}>25</Option>
															<Option value={50}>50</Option>
															<Option value={100}>100</Option>
														</Select>
													</FormControl>
												</Grid>
												<Grid item>
													<Typography>
														{getPageIndicator}
													</Typography>
												</Grid>
												<Grid item>
													<IconButton
														size="sm"
														color="neutral"
														variant="outlined"
														disabled={pagination.page <= 1 || isTasksLoading}
														onClick={handlePreviousPage}
													>
														<KeyboardArrowLeftIcon />
													</IconButton>
													<IconButton
														size="sm"
														color="neutral"
														variant="outlined"
														disabled={pagination.page >= Math.ceil(pagination.count / pagination.page_size) || isTasksLoading}
														onClick={handleNextPage}
													>
														<KeyboardArrowRightIcon />
													</IconButton>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</td>
							</tr>
						</tfoot>
					</Table>
				)}
				</Sheet>
			</Box>

		</>
	)
}
export default TaskReportsTable
